import { createContext, useCallback, useContext, useEffect, useRef, useState, type ButtonHTMLAttributes, type ComponentProps, type ReactNode, type SelectHTMLAttributes } from "react";
import { useBlocker } from "@tanstack/react-router";
import { CircleAlert, CircleCheck, Inbox, Info, ShieldCheck, TriangleAlert, X } from "lucide-react";
import { onAuthPrompt } from "../lib/api";
import { mfaSetupURL, redirectToLogin } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { ApiError } from "../lib/api";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "plain" | "critical";
// Button: primary (brand), secondary (white, bordered), plain (ghost),
// critical (danger). 36px tall, 32px small, 10px radius.
export function Button({ variant = "secondary", size, loading, className, children, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm"; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "h-8 px-2.5" : "h-9 px-3.5",
        variant === "primary" && "btn-primary",
        variant === "secondary" && "btn-secondary",
        variant === "plain" && "text-ink-muted hover:bg-muted hover:text-ink",
        variant === "critical" && "btn-critical",
        className,
      )}
    >
      {loading && <Spinner className="size-3.5" />}
      {children}
    </button>
  );
}

export function Spinner({ className = "size-5" }: { className?: string }) {
  return <span role="status" aria-label="loading" className={cx("inline-block animate-spin rounded-full border-2 border-current border-t-transparent", className)} />;
}

export function Card({ title, description, actions, children, className }: {
  title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={cx("surface", className)}>
      {(title || actions) && (
        <header className="flex min-h-9 items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            {title && <h2 className="pt-1.5 text-base font-semibold leading-6 text-ink">{title}</h2>}
            {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

// Page: large title, an optional one-line description, actions on the end
// side. 32px padding on desktop, 16px on phones.
// sticky: the header (title, status badges, primary actions) stays in view
// while the page scrolls (detail pages, redesign.md §8.5).
export function Page({ title, description, back, actions, children, narrow, sticky }: {
  title: ReactNode; description?: ReactNode; back?: ReactNode; actions?: ReactNode; children: ReactNode; narrow?: boolean; sticky?: boolean;
}) {
  return (
    <div className={cx("mx-auto w-full space-y-6 px-4 py-6 sm:px-8 sm:py-8", narrow ? "max-w-3xl" : "max-w-6xl")}>
      <div className={cx("flex flex-wrap items-start justify-between gap-3",
        sticky && "sticky top-14 z-20 -mx-4 -mt-6 border-b border-line bg-white/95 px-4 py-4 backdrop-blur sm:-mx-8 sm:-mt-8 sm:px-8")}>
        <div className="flex min-w-0 items-start gap-1.5">
          {back && <div className="pt-1 sm:pt-1.5">{back}</div>}
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[28px]">{title}</h1>
            {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

type Tone = "info" | "warning" | "critical" | "success";
const tones: Record<Tone, string> = {
  info: "bg-info-bg ring-info/15 text-ink [&_svg]:text-info",
  warning: "bg-warning-bg ring-warning/20 text-ink [&_svg]:text-warning",
  critical: "bg-danger-bg ring-danger/20 text-ink [&_svg]:text-danger",
  success: "bg-success-bg ring-success/20 text-ink [&_svg]:text-success",
};
const toneIcon: Record<Tone, ReactNode> = {
  info: <Info className="size-5 shrink-0" />, warning: <TriangleAlert className="size-5 shrink-0" />,
  critical: <CircleAlert className="size-5 shrink-0" />, success: <CircleCheck className="size-5 shrink-0" />,
};
export function Banner({ tone = "info", children, action }: { tone?: Tone; children: ReactNode; action?: ReactNode }) {
  return (
    <div role={tone === "critical" ? "alert" : "status"} className={cx("flex flex-wrap items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm ring-1", tones[tone])}>
      <div className="flex items-start gap-2">{toneIcon[tone]}<div className="pt-0.5">{children}</div></div>
      {action}
    </div>
  );
}

export function Badge({ tone, children }: { tone?: Tone | "neutral"; children: ReactNode }) {
  return (
    <span className={cx("inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium",
      tone === "success" && "bg-success-bg text-success",
      tone === "warning" && "bg-warning-bg text-warning",
      tone === "critical" && "bg-danger-bg text-danger",
      tone === "info" && "bg-info-bg text-info",
      (!tone || tone === "neutral") && "bg-muted text-ink-muted")}>
      {tone && tone !== "neutral" && <span aria-hidden className="size-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
}

export function Field({ label, hint, error, children, optional }: { label: ReactNode; hint?: ReactNode; error?: string; children: ReactNode; optional?: boolean }) {
  const { t } = useI18n();
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-stone-900">
        {label} {optional && <span className="font-normal text-stone-500">({t("common.optional")})</span>}
      </span>
      {children}
      {hint && !error && <span className="block text-xs text-stone-500">{hint}</span>}
      {error && <span className="flex items-center gap-1 text-xs text-red-700"><CircleAlert className="size-3.5" />{error}</span>}
    </label>
  );
}

const inputCls = "field";
export function Input(props: ComponentProps<"input">) {
  return <input {...props} className={cx(inputCls, props.className)} />;
}
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputCls, props.className)} />;
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(inputCls, props.className)} />;
}

export function EmptyState({ title, body, action, icon }: { title: ReactNode; body?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-brand-tint text-brand [&_svg]:size-6" aria-hidden>{icon ?? <Inbox className="size-6" strokeWidth={1.75} />}</div>
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      {body && <p className="max-w-sm text-sm text-ink-muted">{body}</p>}
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="animate-pulse space-y-3" aria-busy>
      {Array.from({ length: lines }, (_, i) => <div key={i} className="h-4 rounded-md bg-muted" style={{ width: `${90 - i * 15}%` }} />)}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useI18n();
  return (
    <Banner tone="critical" action={retry && <Button onClick={retry}>{t("common.retry")}</Button>}>
      {error instanceof Error ? error.message : t("common.error")}
    </Banner>
  );
}

export function fieldError(err: unknown, field: string): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const fe = err.errors.find((e) => e.field === field);
  return fe ? fe.message || err.message : undefined;
}

const modalStack: object[] = [];

// Modal: centred, 18px radius. variant="sheet" slides in from the end side
// (create/edit forms); content and behaviour are the same.
export function Modal({ open, title, onClose, children, footer, size = "md", variant = "dialog" }: {
  open: boolean; title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; size?: "sm" | "md" | "full"; variant?: "dialog" | "sheet";
}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    // Escape closes only the top-most modal (a confirm over a wizard).
    const token = {};
    modalStack.push(token);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && modalStack.at(-1) === token && closeRef.current();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      modalStack.splice(modalStack.indexOf(token), 1);
    };
  }, [open]);
  if (!open) return null;
  const sheet = variant === "sheet";
  return (
    <div className={cx("fixed inset-0 z-50 flex bg-ink/20 backdrop-blur-[2px]", sheet ? "justify-end" : "items-end justify-center p-0 sm:items-center sm:p-4")}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal aria-label={typeof title === "string" ? title : undefined}
        className={cx("flex w-full flex-col overflow-hidden bg-white shadow-pop",
          sheet ? "anim-sheet h-full max-w-lg rounded-s-2xl" : cx("anim-pop rounded-t-2xl sm:rounded-2xl", size === "sm" ? "max-w-md" : size === "full" ? "max-w-6xl" : "max-w-lg"))}>
        <header className="flex items-center justify-between gap-3 px-6 py-5 text-lg font-semibold">
          {title}
          <button type="button" aria-label="Close" onClick={onClose} className="rounded-md p-1 text-ink-muted hover:bg-muted hover:text-ink"><X className="size-4" /></button>
        </header>
        <div className={cx("space-y-4 overflow-y-auto px-6 pb-6", sheet ? "flex-1" : size === "full" ? "max-h-[80vh]" : "max-h-[70vh]")}>{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-line bg-subtle px-6 py-3.5">{footer}</footer>}
      </div>
    </div>
  );
}

// ---------- confirm dialog (replaces window.confirm) ----------
export type ConfirmOptions = { title: ReactNode; body?: ReactNode; confirmLabel?: string; tone?: "primary" | "critical" };

let openConfirm: ((o: ConfirmOptions) => Promise<boolean>) | null = null;

// confirmDialog asks in an in-app dialog and resolves true on confirm.
export function confirmDialog(o: ConfirmOptions): Promise<boolean> {
  return openConfirm ? openConfirm(o) : Promise.resolve(window.confirm(String(o.title)));
}

type AuthPrompt = "step_up_required" | "mfa_required";

// DialogProvider hosts confirmDialog and the security prompts the api raises
// (re-authenticate for sensitive actions, or turn on two-step verification).
export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [confirm, setConfirm] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const [auth, setAuth] = useState<{ code: AuthPrompt; returnTo?: string } | null>(null);

  useEffect(() => {
    openConfirm = (o) => new Promise<boolean>((resolve) => {
      resolver.current?.(false);
      resolver.current = resolve;
      setConfirm(o);
    });
    const off = onAuthPrompt((code, returnTo) => setAuth({ code, returnTo }));
    return () => { openConfirm = null; off(); };
  }, []);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setConfirm(null);
  }, []);
  const closeAuth = useCallback(() => setAuth(null), []);

  return (
    <>
      {children}
      {confirm && (
        <Modal open size="sm" title={confirm.title} onClose={() => settle(false)}
          footer={<>
            <Button onClick={() => settle(false)}>{t("common.cancel")}</Button>
            <Button autoFocus variant={confirm.tone === "critical" ? "critical" : "primary"} onClick={() => settle(true)}>{confirm.confirmLabel ?? t("common.continue")}</Button>
          </>}>
          {confirm.body && <div className="text-sm text-stone-600">{confirm.body}</div>}
        </Modal>
      )}
      {auth && (
        <Modal open size="sm" title={t(auth.code === "mfa_required" ? "security.mfa_title" : "security.stepup_title")} onClose={closeAuth}
          footer={<>
            <Button onClick={closeAuth}>{t("common.cancel")}</Button>
            {auth.code === "mfa_required"
              ? <a href={mfaSetupURL(auth.returnTo)} className="inline-flex h-8 items-center rounded-md px-3 text-sm font-medium btn-primary">{t("security.mfa_action")}</a>
              : <Button autoFocus variant="primary" onClick={() => redirectToLogin({ stepUp: true, returnTo: auth.returnTo })}>{t("security.stepup_action")}</Button>}
          </>}>
          <div className="flex gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-700" aria-hidden><ShieldCheck className="size-4" /></div>
            <p className="text-sm text-stone-600">{t(auth.code === "mfa_required" ? "security.mfa_body" : "security.stepup_body")}</p>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------- toast ----------
type ToastMsg = { id: number; text: string; error?: boolean };
const ToastCtx = createContext<(text: string, error?: boolean) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastMsg[]>([]);
  const show = useCallback((text: string, error = false) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, error }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2">
        {items.map((m) => (
          <div key={m.id} role={m.error ? "alert" : "status"} className="anim-pop flex items-center gap-2 rounded-xl bg-ink px-4 py-3 text-sm font-medium text-white shadow-pop">
            {m.error ? <CircleAlert className="size-4 text-danger-on-dark" /> : <CircleCheck className="size-4 text-success-on-dark" />}{m.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---------- save bar (dirty forms) ----------
export function SaveBar({ dirty, saving, onSave, onDiscard }: { dirty: boolean; saving?: boolean; onSave: () => void; onDiscard: () => void }) {
  const { t } = useI18n();
  useBlocker({
    shouldBlockFn: async () => !(await confirmDialog({ title: t("common.leave_title"), body: t("common.leave_confirm"), confirmLabel: t("common.leave"), tone: "critical" })),
    disabled: !dirty, enableBeforeUnload: dirty,
  });
  if (!dirty) return null;
  // Sticky at the bottom while the form is dirty (redesign.md §7.3).
  return (
    <div className="anim-sheet-up fixed inset-x-0 bottom-0 z-40 flex h-16 items-center justify-between gap-3 border-t border-line bg-white px-4 sm:px-8">
      <span className="flex items-center gap-2 text-sm font-medium text-ink"><span className="size-2 rounded-full bg-warning" aria-hidden />{t("common.unsaved")}</span>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onDiscard} disabled={saving}>{t("common.discard")}</Button>
        <Button variant="primary" onClick={onSave} loading={saving}>{t("common.save")}</Button>
      </div>
    </div>
  );
}

// ---------- settings rows (redesign.md §7.3) ----------
// SettingRows lists two-column rows inside a Card: label and description on
// the start side, the control on the end side, 1px dividers between rows.
export function SettingRows({ children }: { children: ReactNode }) {
  return <div className="-my-5 divide-y divide-line">{children}</div>;
}

// group: the control is a set of radios/checkboxes (rendered as a labelled
// group, not one <label>, so each option keeps its own label).
export function SettingRow({ label, description, error, optional, group, children }: {
  label: ReactNode; description?: ReactNode; error?: string; optional?: boolean; group?: boolean; children: ReactNode;
}) {
  const { t } = useI18n();
  const Tag = group ? "div" : "label";
  return (
    <Tag role={group ? "group" : undefined} className="grid gap-x-8 gap-y-2 py-5 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-start">
      <span className="space-y-0.5">
        <span className={cx("block text-sm font-medium text-ink", !group && "md:pt-2")}>{label} {optional && <span className="font-normal text-ink-subtle">({t("common.optional")})</span>}</span>
        {description && <span className="block text-[13px] text-ink-muted">{description}</span>}
      </span>
      <span className="block space-y-1">
        {children}
        {error && <span className="flex items-center gap-1 text-xs text-danger"><CircleAlert className="size-3.5" />{error}</span>}
      </span>
    </Tag>
  );
}

// SettingCheck: an on/off setting as a row — label and description on the
// start side, the checkbox on the end side.
export function SettingCheck({ label, description, checked, disabled, onChange, children }: {
  label: ReactNode; description?: ReactNode; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; children?: ReactNode;
}) {
  return (
    <div className="py-5">
      <label className="flex items-start justify-between gap-6">
        <span className="space-y-0.5">
          <span className="block text-sm font-medium text-ink">{label}</span>
          {description && <span className="block text-[13px] text-ink-muted">{description}</span>}
        </span>
        <input type="checkbox" className="mt-0.5 size-4 shrink-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      </label>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

// Switch: an on/off toggle (store features, channels). Brand colour when on.
export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
      className={cx("relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-brand" : "bg-line-strong")}>
      <span className={cx("inline-block size-4 rounded-full bg-white shadow transition-transform", checked ? "translate-x-[18px] rtl:-translate-x-[18px]" : "translate-x-0.5 rtl:-translate-x-0.5")} />
    </button>
  );
}

// Choice: one radio option with an optional description, for SettingRow groups.
export function Choice({ label, description, checked, disabled, onChange, name }: {
  label: ReactNode; description?: ReactNode; checked: boolean; disabled?: boolean; onChange: () => void; name?: string;
}) {
  return (
    <label className="flex items-start gap-3 text-sm">
      <input type="radio" name={name} className="mt-0.5" checked={checked} disabled={disabled} onChange={onChange} />
      <span><span className="font-medium text-ink">{label}</span>{description && <span className="block text-[13px] text-ink-muted">{description}</span>}</span>
    </label>
  );
}

// ---------- index pages (redesign.md §8.4) ----------
// SegmentedViews: saved views / status filters as a segmented pill control
// (replaces text tabs above tables). Same values, same callback.
export function SegmentedViews<T extends string>({ items, value, onChange, label, className }: {
  items: { value: T; label: ReactNode; count?: ReactNode }[]; value: T; onChange: (v: T) => void; label?: string; className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={cx("inline-flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-muted p-1", className)}>
      {items.map((it) => {
        const on = it.value === value;
        return (
          <button key={it.value} type="button" role="tab" aria-selected={on} onClick={() => onChange(it.value)}
            className={cx("inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm",
              on ? "bg-white font-medium text-ink shadow-card ring-1 ring-line" : "text-ink-muted hover:text-ink")}>
            {it.label}
            {it.count !== undefined && it.count !== null && it.count !== 0 && it.count !== "0" && (
              <span className={cx("rounded-full px-1.5 font-mono text-[11px] leading-4", on ? "bg-brand-tint text-brand" : "bg-white/70 text-ink-muted")}>{it.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// BulkBar: actions for selected rows, floating at the bottom of the screen.
export function BulkBar({ label, onClear, children }: { label: ReactNode; onClear?: () => void; children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div role="region" aria-label={typeof label === "string" ? label : undefined}
      className="anim-pop fixed inset-x-0 bottom-6 z-40 mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center gap-2 rounded-2xl bg-ink px-3 py-2 text-sm text-white shadow-pop">
      {onClear && (
        <button type="button" onClick={onClear} aria-label={t("common.cancel")} className="flex size-7 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white">
          <X className="size-4" />
        </button>
      )}
      <span className="pe-2 font-medium">{label}</span>
      {children}
    </div>
  );
}
